import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { EmergencyContact } from '@/services/scheduling/use-prebook-selected-patient';

/**
 * POST /api/Patients/EmergencyContact?PatientId=<id>
 *
 * Body shape mirrors what dev-practice.scrips.com sends today (verified
 * against a working curl). Critical details:
 *
 *   - `relation` ships with PascalCase keys (Id / Name / System / Code) —
 *     lowercase fails backend deserialisation in the create path.
 *   - For NEW rows: omit `emergencyContactId`, send `relationship: null`,
 *     `submittedDate: null`. The backend resolves the relation via
 *     `relation.Id`.
 *   - For EXISTING rows: include `emergencyContactId`, ship the existing
 *     UUID for `relationship`, keep whatever `submittedDate` was last set.
 *   - The Self row (where `emergencyContactId === patientId` or
 *     `relation.Name === "Self"`) is filtered out — dev drops it.
 *   - No `name` field anywhere; backend computes display from
 *     firstName + lastName.
 *   - Address/apartment/etc. always present (`null` when blank) so the
 *     backend's IsEmergencyAddressChanged path doesn't NRE.
 */

interface SaveContactsParams {
  patientId: string;
  contacts: EmergencyContact[];
}

interface DevContactRow {
  emergencyContactId?: string;
  patientId: string;
  photoUrl: string | null;
  relationship: string | null;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  contactNumber: string | null;
  isProxy: boolean;
  isDeleted: boolean;
  relation: { Id?: string; Name?: string; System?: string | null; Code?: string | null } | null;
  submittedDate: string | null;
  building: string | null;
  apartment: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  latitude: string | null;
  longitude: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useSaveEmergencyContacts() {
  return useMutation<EmergencyContact[], Error, SaveContactsParams>({
    mutationFn: async ({ patientId, contacts }) => {
      const cleaned: DevContactRow[] = contacts
        .filter((c) => {
          const fn = (c.firstName ?? '').trim();
          const ln = (c.lastName ?? '').trim();
          const num = (c.contactNumber ?? c.phone ?? '').trim();
          if (!fn || !ln || !num) return false;
          // Drop Self — dev filters this row out (it's the patient
          // themselves, persisted as a synthetic emergency contact).
          const eid = (c.emergencyContactId ?? c.id ?? '').toLowerCase();
          if (eid === patientId.toLowerCase()) return false;
          const rel = (c.relation?.name ?? c.relationship ?? '').trim().toLowerCase();
          if (rel === 'self') return false;
          return true;
        })
        .map((c): DevContactRow => {
          const eid = (c.emergencyContactId ?? c.id ?? '').trim();
          const isNew = !eid || eid.startsWith('local-') || !UUID_RE.test(eid);

          // Resolve the relation object. Existing rows already carry the
          // master-list shape (we just up-case the keys); new rows expect
          // a Relationship object passed in `c.relation` (with id/name).
          // Preserve System/Code when the upstream entry provides them —
          // dev's curl ships null for the common rows but the master list
          // can include populated value-set codes which the backend
          // validates against on save.
          const relIn = c.relation;
          const relationOut =
            relIn && (relIn.id || relIn.name)
              ? {
                  Id: relIn.id,
                  Name: relIn.name,
                  System: relIn.system ?? null,
                  Code: relIn.code ?? null,
                }
              : null;

          // For existing rows the backend stores `relationship` as the
          // UUID. For new rows dev sends null and lets the backend
          // hydrate from `relation.Id`.
          const relationship = isNew
            ? null
            : (c.relationship ?? relationOut?.Id ?? null);

          const cWithAddr = c as EmergencyContact & {
            building?: string | null;
            apartment?: string | null;
            country?: string | null;
            city?: string | null;
            address?: string | null;
            latitude?: string | null;
            longitude?: string | null;
            submittedDate?: string | null;
          };

          const row: DevContactRow = {
            patientId,
            photoUrl: c.photoUrl ?? c.photo ?? (isNew ? null : ''),
            relationship,
            firstName: c.firstName ?? null,
            lastName: c.lastName ?? null,
            middleName: c.middleName ?? null,
            contactNumber: c.contactNumber ?? c.phone ?? null,
            isProxy: false,
            isDeleted: false,
            relation: relationOut,
            submittedDate: cWithAddr.submittedDate ?? null,
            building: cWithAddr.building ?? null,
            apartment: cWithAddr.apartment ?? null,
            country: cWithAddr.country ?? null,
            city: cWithAddr.city ?? null,
            address: cWithAddr.address ?? null,
            latitude: cWithAddr.latitude ?? null,
            longitude: cWithAddr.longitude ?? null,
          };
          if (!isNew) row.emergencyContactId = eid;
          return row;
        });

      return post<EmergencyContact[]>(
        `/api/Patients/EmergencyContact?PatientId=${encodeURIComponent(patientId)}`,
        cleaned,
      );
    },
  });
}
